/**
 * gateTelemetry — per-door gate-rejection telemetry for the promo-code signup
 * gate (NFR-8).
 *
 * Implements promo-code US-005 (T1); AC-005.7. Metrics/logging only — no
 * user-facing surface. Emitted once per door when the RegisterPage gate
 * (US-005 T3) transitions into a settled-invalid disabled state, so ops can
 * judge whether to flip the kill switch (US-006).
 *
 * Security (US-005 security_considerations): NEVER logs the raw code value,
 * only its length, so a shareable promo/referral code cannot leak into logs.
 *
 * Follows the shipped console.error structured-logging idiom already used in
 * ContinueWithGoogleButton.tsx (see the signInWithRedirect failure log at
 * '[ContinueWithGoogleButton] signInWithRedirect failed').
 *
 * BUG-20260722-000251 (T-6, RC-002): this was the RCA's named false-confidence
 * artifact — its docblock promised ops visibility but console.info never left
 * the device. Now ALSO routed through the shared errorReporter as a LOG-ONLY
 * 'event' (never increments the ClientError metric), so ops actually receive
 * it. Signature, console.info, and the length-only privacy guarantee are all
 * unchanged (src/promo-code-US-005/RegisterPage.gate.test.tsx AC-005.7 spies
 * on this function's own call args, not on the reporter).
 */

import { reportClientEvent } from '@/lib/errorReporter';

/** Which submit door the gate rejected the current code on. */
export type OAuthDoor = 'native' | 'google';

const GATE_REJECTION_EVENT = 'promo_gate_rejection';

/**
 * Emit a single structured, advisory telemetry line for a per-door
 * gate-rejection. Implements US-005 (T1) / AC-005.7.
 *
 * Enforces TO-LL-036 in its documented advisory-exception form: the try/catch
 * here swallows any logging failure deliberately, because this is an
 * observability sink with no real data-integrity operation to protect — a
 * failed console write must never break the signup UI. This is NOT masking a
 * genuine infra failure; it is the advisory-exception carve-out TO-LL-036
 * itself describes.
 *
 * @param door 'native' (Sign Up button) or 'google' (Continue with Google).
 * @param codeLength the LENGTH of the trimmed code, never the raw code.
 */
export function emitGateRejection(door: OAuthDoor, codeLength: number | undefined): void {
  try {
    const safeLength =
      typeof codeLength === 'number' && Number.isFinite(codeLength) ? codeLength : 0;
    // Deliberately no raw code in this payload — door + length only.
    console.info(GATE_REJECTION_EVENT, { door, codeLength: safeLength, ts: Date.now() });
    // BUG-20260722-000251 (T-6): log-only event — never increments ClientError.
    // door/codeLength are composed into the allowlisted `message` field
    // (errorReporter.reportClientEvent only reads name/message/componentStack/
    // routePattern from its data argument — any other key is ignored, never
    // smuggled onto the wire), preserving the same length-only privacy
    // guarantee as the console.info call above.
    reportClientEvent(GATE_REJECTION_EVENT, { message: `door=${door} codeLength=${safeLength}` });
  } catch {
    // Advisory sink; never throw into the render path (TO-LL-036 exception form).
  }
}
